import React from 'react';
import {
  EDU_TIME_HOUR12_OPTIONS,
  EDU_TIME_MINUTE5_OPTIONS,
  hour24ToParts,
  nearestMinute5Option,
  parseHHMM,
  partsToHour24,
  stepTimeByFiveMinutes,
  toHHMM,
} from '../../utils/eduTimeInput';

const PICKER_ITEM_HEIGHT = 36;
const PICKER_VISIBLE_ROWS = 5;
const PICKER_PAD = Math.floor(PICKER_VISIBLE_ROWS / 2) * PICKER_ITEM_HEIGHT;

const DEFAULT_EMPTY_HOUR12 = 9;
const DEFAULT_EMPTY_MINUTE = 0;

function clampInt(raw: string, min: number, max: number): number | null {
  const digits = raw.replace(/\D/g, '');
  if (!digits) return null;
  const n = Number(digits);
  if (!Number.isFinite(n)) return null;
  return Math.min(max, Math.max(min, n));
}

function commitParts(isPm: boolean, hour12: number, minute: number): string {
  return toHHMM(partsToHour24(isPm, hour12), minute);
}

const BoundedScrollColumn: React.FC<{
  items: number[];
  value: number;
  onChange: (next: number) => void;
  format: (n: number) => string;
  ariaLabel: string;
}> = ({ items, value, onChange, format, ariaLabel }) => {
  const scrollerRef = React.useRef<HTMLDivElement>(null);
  const syncingRef = React.useRef(false);

  const scrollToValue = React.useCallback(
    (target: number, behavior: ScrollBehavior = 'auto') => {
      const el = scrollerRef.current;
      if (!el) return;
      const idx = items.indexOf(target);
      if (idx < 0) return;
      syncingRef.current = true;
      el.scrollTo({ top: idx * PICKER_ITEM_HEIGHT, behavior });
      window.requestAnimationFrame(() => {
        syncingRef.current = false;
      });
    },
    [items]
  );

  React.useEffect(() => {
    scrollToValue(value);
  }, [value, scrollToValue]);

  const syncFromScroll = () => {
    if (syncingRef.current) return;
    const el = scrollerRef.current;
    if (!el) return;
    const idx = Math.round(el.scrollTop / PICKER_ITEM_HEIGHT);
    const clamped = Math.max(0, Math.min(items.length - 1, idx));
    const next = items[clamped];
    if (next !== value) onChange(next);
    if (clamped !== idx) scrollToValue(next);
  };

  const handleWheel = (e: React.WheelEvent<HTMLDivElement>) => {
    const el = scrollerRef.current;
    if (!el) return;
    const maxScroll = el.scrollHeight - el.clientHeight;
    const atTop = el.scrollTop <= 0;
    const atBottom = el.scrollTop >= maxScroll - 1;
    if ((atTop && e.deltaY < 0) || (atBottom && e.deltaY > 0)) {
      e.preventDefault();
    }
  };

  return (
    <div
      ref={scrollerRef}
      className="cp-edu-time-scroll-col"
      role="listbox"
      aria-label={ariaLabel}
      onScroll={syncFromScroll}
      onWheel={handleWheel}
    >
      <div className="cp-edu-time-scroll-pad" aria-hidden />
      {items.map((item) => (
        <div
          key={item}
          className={`cp-edu-time-scroll-item${item === value ? ' is-selected' : ''}`}
          role="option"
          aria-selected={item === value}
        >
          {format(item)}
        </div>
      ))}
      <div className="cp-edu-time-scroll-pad" aria-hidden />
    </div>
  );
};

const ClassScheduleTimeInput: React.FC<{
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  required?: boolean;
  ariaLabel: string;
}> = ({ value, onChange, disabled = false, required = false, ariaLabel }) => {
  const rootRef = React.useRef<HTMLDivElement>(null);
  const focusedRef = React.useRef(false);
  const [pickerOpen, setPickerOpen] = React.useState(false);

  const parsed = parseHHMM(value);
  const parts = parsed ? hour24ToParts(parsed.hour24) : { isPm: false, hour12: DEFAULT_EMPTY_HOUR12 };

  const [hourText, setHourText] = React.useState(() =>
    parsed ? String(parts.hour12).padStart(2, '0') : ''
  );
  const [minuteText, setMinuteText] = React.useState(() =>
    parsed ? String(parsed.minute).padStart(2, '0') : ''
  );
  const [isPm, setIsPm] = React.useState(parts.isPm);

  const [draftIsPm, setDraftIsPm] = React.useState(parts.isPm);
  const [draftHour12, setDraftHour12] = React.useState(parts.hour12);
  const [draftMinute, setDraftMinute] = React.useState(
    parsed ? nearestMinute5Option(parsed.minute) : 0
  );

  React.useEffect(() => {
    if (focusedRef.current || pickerOpen) return;
    if (!parsed) {
      setHourText('');
      setMinuteText('');
      setIsPm(false);
      setDraftIsPm(false);
      setDraftHour12(DEFAULT_EMPTY_HOUR12);
      setDraftMinute(0);
      return;
    }
    const p = hour24ToParts(parsed.hour24);
    setHourText(String(p.hour12).padStart(2, '0'));
    setMinuteText(String(parsed.minute).padStart(2, '0'));
    setIsPm(p.isPm);
    setDraftIsPm(p.isPm);
    setDraftHour12(p.hour12);
    setDraftMinute(nearestMinute5Option(parsed.minute));
  }, [value, parsed, pickerOpen]);

  React.useEffect(() => {
    if (!pickerOpen) return;
    const onDocMouseDown = (e: MouseEvent) => {
      if (rootRef.current?.contains(e.target as Node)) return;
      setPickerOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setPickerOpen(false);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [pickerOpen]);

  const commit = (nextIsPm: boolean, hourRaw: string, minuteRaw: string, allowEmpty = false) => {
    const hourEmpty = !hourRaw.replace(/\D/g, '');
    const minuteEmpty = !minuteRaw.replace(/\D/g, '');
    if (allowEmpty && hourEmpty && minuteEmpty) {
      setHourText('');
      setMinuteText('');
      setIsPm(nextIsPm);
      onChange('');
      return;
    }
    const hour12 = clampInt(hourRaw, 1, 12) ?? DEFAULT_EMPTY_HOUR12;
    const minute = clampInt(minuteRaw, 0, 59) ?? DEFAULT_EMPTY_MINUTE;
    setHourText(String(hour12).padStart(2, '0'));
    setMinuteText(String(minute).padStart(2, '0'));
    setIsPm(nextIsPm);
    setDraftIsPm(nextIsPm);
    setDraftHour12(hour12);
    setDraftMinute(nearestMinute5Option(minute));
    onChange(commitParts(nextIsPm, hour12, minute));
  };

  const applyDraft = (next: { isPm?: boolean; hour12?: number; minute?: number }) => {
    const nextIsPm = next.isPm ?? draftIsPm;
    const hour12 = next.hour12 ?? draftHour12;
    const min = next.minute ?? draftMinute;
    setDraftIsPm(nextIsPm);
    setDraftHour12(hour12);
    setDraftMinute(min);
    setHourText(String(hour12).padStart(2, '0'));
    setMinuteText(String(min).padStart(2, '0'));
    setIsPm(nextIsPm);
    onChange(commitParts(nextIsPm, hour12, min));
  };

  const handleBlur = () => {
    focusedRef.current = false;
    commit(isPm, hourText, minuteText, true);
  };

  const handleDigitKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' || e.key === 'Delete') {
      e.preventDefault();
      setHourText('');
      setMinuteText('');
    }
  };

  const openPicker = () => {
    if (disabled) return;
    if (parsed) {
      const p = hour24ToParts(parsed.hour24);
      setDraftIsPm(p.isPm);
      setDraftHour12(p.hour12);
      setDraftMinute(nearestMinute5Option(parsed.minute));
    } else {
      const hour12 = clampInt(hourText, 1, 12) ?? DEFAULT_EMPTY_HOUR12;
      const minute = clampInt(minuteText, 0, 59) ?? DEFAULT_EMPTY_MINUTE;
      setDraftIsPm(isPm);
      setDraftHour12(hour12);
      setDraftMinute(nearestMinute5Option(minute));
    }
    setPickerOpen(true);
  };

  const toggleAmPm = () => {
    if (disabled) return;
    const hour12 = clampInt(hourText, 1, 12) ?? DEFAULT_EMPTY_HOUR12;
    const minute = clampInt(minuteText, 0, 59) ?? DEFAULT_EMPTY_MINUTE;
    commit(!isPm, String(hour12), String(minute));
  };

  const stepMinute = (direction: 1 | -1) => {
    if (disabled) return;
    const hour12 = clampInt(hourText, 1, 12) ?? DEFAULT_EMPTY_HOUR12;
    const minute = clampInt(minuteText, 0, 59) ?? DEFAULT_EMPTY_MINUTE;
    const hour24 = partsToHour24(isPm, hour12);
    const next = stepTimeByFiveMinutes(hour24, minute, direction);
    const p = hour24ToParts(next.hour24);
    commit(p.isPm, String(p.hour12), String(next.minute));
  };

  return (
    <div
      ref={rootRef}
      className={`cp-edu-time-input${disabled ? ' is-disabled' : ''}${pickerOpen ? ' is-picker-open' : ''}`}
      aria-required={required || undefined}
    >
      <button
        type="button"
        className="cp-edu-time-ampm"
        disabled={disabled}
        aria-label="오전/오후 전환"
        onClick={toggleAmPm}
      >
        {isPm ? '오후' : '오전'}
      </button>
      <span className="cp-edu-time-clock is-edit">
        <input
          type="text"
          inputMode="numeric"
          className="cp-edu-time-clock-digit"
          value={hourText}
          placeholder="09"
          disabled={disabled}
          aria-label={`${ariaLabel} 시`}
          maxLength={2}
          onFocus={(e) => {
            focusedRef.current = true;
            e.currentTarget.select();
          }}
          onBlur={handleBlur}
          onKeyDown={handleDigitKeyDown}
          onChange={(e) => {
            const digits = e.target.value.replace(/\D/g, '').slice(0, 2);
            setHourText(digits);
            if (digits.length === 2) {
              const hour12 = clampInt(digits, 1, 12) ?? DEFAULT_EMPTY_HOUR12;
              const minute = clampInt(minuteText, 0, 59) ?? DEFAULT_EMPTY_MINUTE;
              commit(isPm, String(hour12), String(minute));
            }
          }}
        />
        <span className="cp-edu-time-clock-sep">:</span>
        <input
          type="text"
          inputMode="numeric"
          className="cp-edu-time-clock-digit"
          value={minuteText}
          placeholder="00"
          disabled={disabled}
          aria-label={`${ariaLabel} 분`}
          maxLength={2}
          onFocus={(e) => {
            focusedRef.current = true;
            e.currentTarget.select();
          }}
          onBlur={handleBlur}
          onKeyDown={handleDigitKeyDown}
          onChange={(e) => {
            const digits = e.target.value.replace(/\D/g, '').slice(0, 2);
            setMinuteText(digits);
            if (digits.length === 2) {
              const hour12 = clampInt(hourText, 1, 12) ?? DEFAULT_EMPTY_HOUR12;
              const minute = clampInt(digits, 0, 59) ?? DEFAULT_EMPTY_MINUTE;
              commit(isPm, String(hour12), String(minute));
            }
          }}
        />
      </span>
      <div className="cp-edu-time-step-group">
        <button
          type="button"
          className="cp-edu-time-step cp-edu-time-step-up"
          disabled={disabled}
          aria-label={`${ariaLabel} 5분 후`}
          onClick={() => stepMinute(1)}
        >
          ▲
        </button>
        <button
          type="button"
          className="cp-edu-time-step cp-edu-time-step-down"
          disabled={disabled}
          aria-label={`${ariaLabel} 5분 전`}
          onClick={() => stepMinute(-1)}
        >
          ▼
        </button>
      </div>
      <button
        type="button"
        className="cp-edu-time-picker-btn"
        disabled={disabled}
        aria-label={`${ariaLabel} 시계로 선택`}
        aria-expanded={pickerOpen}
        onClick={() => (pickerOpen ? setPickerOpen(false) : openPicker())}
      >
        <svg width="20" height="20" viewBox="0 0 16 16" aria-hidden focusable="false">
          <circle cx="8" cy="8" r="6.25" fill="none" stroke="currentColor" strokeWidth="1.35" />
          <path d="M8 4.5V8l2.5 1.5" stroke="currentColor" strokeWidth="1.35" strokeLinecap="round" />
        </svg>
      </button>
      {pickerOpen ? (
        <div className="cp-edu-time-picker-popover" role="dialog" aria-label={`${ariaLabel} 선택`}>
          <div className="cp-edu-time-picker-head">
            <button
              type="button"
              className={`cp-edu-time-picker-ampm${draftIsPm ? ' is-pm' : ' is-am'}`}
              onClick={() => applyDraft({ isPm: !draftIsPm })}
            >
              {draftIsPm ? '오후' : '오전'}
            </button>
          </div>
          <div
            className="cp-edu-time-picker-columns"
            style={{ ['--cp-edu-time-picker-pad' as string]: `${PICKER_PAD}px` }}
          >
            <BoundedScrollColumn
              items={[...EDU_TIME_HOUR12_OPTIONS]}
              value={draftHour12}
              onChange={(hour12) => applyDraft({ hour12 })}
              format={(n) => String(n).padStart(2, '0')}
              ariaLabel="시"
            />
            <span className="cp-edu-time-picker-sep">:</span>
            <BoundedScrollColumn
              items={EDU_TIME_MINUTE5_OPTIONS}
              value={draftMinute}
              onChange={(minute) => applyDraft({ minute })}
              format={(n) => String(n).padStart(2, '0')}
              ariaLabel="분"
            />
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default ClassScheduleTimeInput;
